// pasteIntent.js — PURE classifier for clipboard paste into the chat composer.
//
// Decides what a Cmd+V paste "means" so the composer can react smartly:
//   - image in clipboard (screenshot / copied image) → attach it, don't dump
//     base64 garbage into the textarea.
//   - very large text → handled gracefully (caller may paste-as-file/snippet)
//     instead of blowing the composer.
//   - normal/small text → leave native paste completely alone.
//
// PURE on purpose: takes a minimal clipboard-like shape ({ items, getData })
// — the real `event.clipboardData` satisfies it — so it's unit-testable
// without a DOM ClipboardEvent. Never throws; unknown/garbage input → 'text'
// (the safe no-op that falls through to native paste).

// Texto acima disso é "grande demais" pra digitar à mão — provável colagem de
// um arquivo/log inteiro. Nomeado pra ser tunável num só lugar.
export const LARGE_PASTE_CHARS = 2000;

/**
 * @param {{ items?: ArrayLike<DataTransferItem>, getData?: (fmt: string) => string }} clipboardData
 * @returns {{ kind: 'image'|'large-text'|'text', files?: File[], text?: string }}
 */
export function classifyPaste(clipboardData) {
  if (!clipboardData || typeof clipboardData !== 'object') {
    return { kind: 'text', text: '' };
  }

  // 1) Image wins. Walk the DataTransferItemList for image/* file items and
  //    materialize their File objects. A screenshot/copied image lands here.
  const files = [];
  const items = clipboardData.items;
  if (items && typeof items.length === 'number') {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item) continue;
      const type = item.type || '';
      const isImage = type.startsWith('image/');
      if (!isImage) continue;
      try {
        const file = typeof item.getAsFile === 'function' ? item.getAsFile() : null;
        if (file) files.push(file);
      } catch {
        // getAsFile can throw on detached items — skip, never blow up paste.
      }
    }
  }
  if (files.length > 0) {
    return { kind: 'image', files };
  }

  // 2) Text branch. Read the plain-text payload defensively.
  let text = '';
  if (typeof clipboardData.getData === 'function') {
    try {
      text = clipboardData.getData('text/plain') || clipboardData.getData('text') || '';
    } catch {
      text = '';
    }
  }
  text = typeof text === 'string' ? text : '';

  if (text.length > LARGE_PASTE_CHARS) {
    return { kind: 'large-text', text };
  }
  return { kind: 'text', text };
}
